if (!customElements.get("sticky-atc")) {
  customElements.define(
    "sticky-atc",
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          prodTitle: "[data-enova-sticky-title]",
          addToCart: ".m-add-to-cart",
          buyNowBtn: ".m-product-dynamic-checkout",
          variantIdSelect: '[name="id"]',
          foxkitBtn: ".foxkit-button",
          optionSelects: "[data-enova-sticky-option]",
          price: "[data-enova-sticky-price]",
        };
        this.hasCustomFields = !!document.querySelector(".m-main-product--info .m-product-custom-field");
        this.visible = false;
        this.hasSeenAtc = false;
      }

      connectedCallback() {
        if (this.hasInitialized) return;
        this.hasInitialized = true;

        this.mainProduct = document.querySelector("product-info[id^='MainProduct']");
        this.container = this.closest(".m-sticky-addtocart");
        this.mainProductForm = document.querySelector(".m-product-form--main");
        this.mainProductInfo = document.querySelector(".m-main-product--info");
        this.mainAddToCart = this.mainProductForm ? this.mainProductForm.querySelector(".m-add-to-cart") : null;
        this.mainDynamicCheckout = this.mainProductForm
          ? this.mainProductForm.querySelector(this.selectors.buyNowBtn)
          : null;
        this.disableSelectedVariantDefault =
          (this.mainProduct && this.mainProduct.dataset.disableSelectedVariantDefault === "true") || false;
        this.domNodes = queryDomNodes(this.selectors, this.container);

        this.init();
      }

      disconnectedCallback() {
        this.teardownAtcObserver();
        if (this.mql) this.mql.removeEventListener("change", this.onViewportChange);
        window.removeEventListener("resize", this.onResize);
        window.removeEventListener("scroll", this.onScroll);
      }

      init() {
        if (!this.mainAddToCart || !this.container) return;

        this.variantData = this.getVariantData();
        this.onViewportChange = this.handleViewportChange.bind(this);
        this.onResize = this.setStickyAddToCartHeight.bind(this);
        this.onScroll = this.handleScroll.bind(this);

        if (this.disableSelectedVariantDefault) {
          this.handleDisableSelectedVariantDefault();
        }

        if (this.domNodes.prodTitle) {
          this.domNodes.prodTitle.addEventListener("click", () => __scrollToTop());
        }

        this.bindOptionSelects();
        this.handleCustomFields();
        this.syncWithMainProductForm();

        this.mql = window.matchMedia(MinimogTheme.config.mediaQueryMobile);
        this.mql.addEventListener("change", this.onViewportChange);
        window.addEventListener("resize", this.onResize);
        window.addEventListener("scroll", this.onScroll, { passive: true });
        this.setStickyAddToCartHeight();
        this.initVisibility();
      }

      isEnabledForViewport() {
        const showOnDesktop = this.container.dataset.showOnDesktop === "true";
        const showOnMobile = this.container.dataset.showOnMobile === "true";
        const isMobile = window.matchMedia(MinimogTheme.config.mediaQueryMobile).matches;
        return isMobile ? showOnMobile : showOnDesktop;
      }

      initVisibility() {
        this.teardownAtcObserver();
        if (!this.isEnabledForViewport()) {
          this.setStickyVisible(false);
          return;
        }
        this.initAtcObserver();
        this.updateVisibilityFromAtc();
      }

      initAtcObserver() {
        if (!this.mainAddToCart || typeof IntersectionObserver === "undefined") return;

        this.atcObserver = new IntersectionObserver(() => {
          this.updateVisibilityFromAtc();
        }, { root: null, threshold: 0 });
        this.atcObserver.observe(this.mainAddToCart);
      }

      teardownAtcObserver() {
        if (!this.atcObserver) return;
        this.atcObserver.disconnect();
        this.atcObserver = null;
      }

      updateVisibilityFromAtc() {
        if (!this.mainAddToCart || !this.isEnabledForViewport()) {
          this.setStickyVisible(false);
          return;
        }

        const rect = this.mainAddToCart.getBoundingClientRect();
        const inView = rect.bottom > 0 && rect.top < window.innerHeight;

        if (inView) this.hasSeenAtc = true;

        this.setStickyVisible(this.hasSeenAtc && rect.bottom < 0);
      }

      handleScroll() {
        if (this.scrollTick) return;
        this.scrollTick = true;
        requestAnimationFrame(() => {
          this.scrollTick = false;
          this.updateVisibilityFromAtc();
        });
      }

      setStickyVisible(visible) {
        if (!this.isEnabledForViewport()) visible = false;
        if (this.visible === visible) return;
        this.visible = visible;
        document.documentElement.classList.toggle("stick-atc-show", visible);
        this.container.setAttribute("aria-hidden", visible ? "false" : "true");
        this.container.toggleAttribute("inert", !visible);
        this.setStickyAddToCartHeight();
      }

      handleViewportChange() {
        this.initVisibility();
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      bindOptionSelects() {
        const selects = this.querySelectorAll(this.selectors.optionSelects);
        selects.forEach((select) => {
          select.addEventListener("change", () => {
            if (this.syncingFromPicker) return;
            this.selectOptionInPicker(select);
          });
        });
      }

      selectOptionInPicker(select) {
        const picker = this.mainProduct && this.mainProduct.querySelector("variant-picker");
        if (!picker) return;

        const optionValueId = select.options[select.selectedIndex] && select.options[select.selectedIndex].dataset.optionValueId;
        if (!optionValueId) return;

        const match = Array.from(picker.querySelectorAll("[data-option-value-id]")).find(
          (el) => el.getAttribute("data-option-value-id") === optionValueId
        );
        if (!match) return;

        if (match.tagName === "OPTION") {
          const parent = match.parentElement;
          parent.value = match.value;
          parent.dispatchEvent(new Event("change", { bubbles: true }));
          return;
        }

        if (match.tagName === "INPUT") {
          match.checked = true;
          match.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      setUnavailable() {
        this.toggleAddButton(true, window.MinimogStrings.unavailable);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector(".m-product-form");
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
        const qtySelect = productForm.querySelector(".enova-atc__qty");
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          if (qtySelect) qtySelect.setAttribute("disabled", "disabled");
          if (text && addButtonText) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute("disabled");
          if (qtySelect) qtySelect.removeAttribute("disabled");
          if (addButtonText) addButtonText.innerHTML = window.MinimogStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      handleDisableSelectedVariantDefault() {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("variant")) return;

        const variantInput = this.querySelector(this.selectors.variantIdSelect);
        if (variantInput) variantInput.value = "";
        this.setUnavailable();
      }

      setStickyAddToCartHeight() {
        const height = this.visible ? this.offsetHeight : 0;
        document.documentElement.style.setProperty("--f-sticky-atc-bar-height", "0px");
        document.documentElement.style.setProperty("--enova-pdp-sticky-height", `${this.offsetHeight || 0}px`);
        window.MinimogSettings.stickyAddToCartHeight = height;
      }

      updateStickyPrice(html, sectionId) {
        if (!html) return;
        const dest = this.querySelector(this.selectors.price);
        const source = html.getElementById(`Price-${sectionId}`);
        if (!dest || !source) return;

        const sourcePrice = source.querySelector(".m-price");
        const destPrice = dest.querySelector(".m-price");
        if (sourcePrice && destPrice) {
          destPrice.replaceWith(sourcePrice.cloneNode(true));
          return;
        }
        dest.innerHTML = source.innerHTML;
      }

      updateOptionSelects(variant) {
        if (!variant || !variant.options) return;
        this.syncingFromPicker = true;
        variant.options.forEach((value, index) => {
          const select = this.querySelector(`${this.selectors.optionSelects}[data-enova-option-index="${index}"]`);
          if (select && value != null) select.value = value;
        });
        this.syncingFromPicker = false;
      }

      syncWithMainProductForm() {
        const variantInput = this.querySelector(this.selectors.variantIdSelect);
        MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, (e) => {
          if (!this.mainProduct) return;
          const isMainProduct = e.data.sectionId === this.mainProduct.dataset.sectionId;
          if (!isMainProduct) return;
          this.currentVariant = e.data.variant;
          this.updateStickyPrice(e.data.html, e.data.sectionId);
          if (this.currentVariant) {
            if (variantInput) variantInput.value = e.data.variant.id;
            this.updateOptionSelects(this.currentVariant);
            this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut);
          } else {
            if (variantInput) variantInput.value = "";
            this.toggleAddButton(true, window.MinimogStrings.unavailable);
          }
        });
      }

      handleCustomFields() {
        if (!this.hasCustomFields) return;

        const { addToCart, buyNowBtn, foxkitBtn } = this.domNodes;

        let hasCustomFieldRequired = false;
        const customFields = document.querySelectorAll(".m-main-product--info .m-product-custom-field");
        customFields &&
          customFields.forEach((item) => {
            const field = item.querySelector(".form-field");
            if (field.value == "" && field.hasAttribute("required")) {
              hasCustomFieldRequired = true;
            }
          });

        hasCustomFieldRequired &&
          addToCart &&
          addToCart.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            __scrollToTop(this.mainProductInfo, () => this.mainAddToCart.click());
          });
        if (buyNowBtn) {
          buyNowBtn.addEventListener(
            "click",
            (e) => {
              const missing = validateForm(this.mainProductInfo);
              if (missing.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                __scrollToTop(this.mainProductInfo, () => this.mainDynamicCheckout.click());
              }
            },
            true
          );
        }
        if (foxkitBtn) {
          foxkitBtn.addEventListener("click", (e) => {
            e.preventDefault();
            __scrollToTop(this.mainProductInfo, () => this.mainAddToCart.click());
          });
        }
      }
    }
  );
}
